import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RemoveIcon from '@mui/icons-material/Remove';
import * as quoteApi from './quoteApi';
import { SchematicMeta } from './quoteApi';

interface SchematicViewerProps {
  quoteId: string;
  lineId: number;
  meta: SchematicMeta;
  page: number | null;
  onPageChange: (page: number) => void;
  popout?: boolean;
}

export const openSchematicWindow = (quoteId: string, lineId: number) => {
  const url = `/estimator/schematic?quoteId=${encodeURIComponent(quoteId)}&lineId=${lineId}`;
  window.open(url, `schematic-${quoteId}-${lineId}`, 'noopener,noreferrer,width=1280,height=900');
};

const SchematicViewer: React.FC<SchematicViewerProps> = ({
  quoteId,
  lineId,
  meta,
  page,
  onPageChange,
  popout = false,
}) => {
  const [zoom, setZoom] = useState(1);
  const pages = meta.all_pages?.length ? meta.all_pages : (meta.pages || []);
  const tagged = useMemo(() => new Set(meta.pages || []), [meta.pages]);
  const current = page || pages[0] || 1;
  const src = quoteApi.schematicImageUrl(quoteId, lineId, current);

  return (
    <Box>
      {!meta.available && (
        <Alert severity="info" sx={{ mb: 1 }}>{meta.reason || 'No schematic for this TAG.'}</Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>TAG {meta.tag || '—'}</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Zoom out">
          <span>
            <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.25) * 100) / 100))} disabled={zoom <= 0.5}>
              <RemoveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption">{Math.round(zoom * 100)}%</Typography>
        <Tooltip title="Zoom in">
          <span>
            <IconButton size="small" onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.25) * 100) / 100))} disabled={zoom >= 3}>
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {!popout && (
          <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => openSchematicWindow(quoteId, lineId)}>
            Open window
          </Button>
        )}
      </Box>
      {pages.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
          {pages.map((item) => (
            <Chip
              key={item}
              size="small"
              label={`Page ${item}${tagged.has(item) ? ' · tag' : ''}`}
              color={current === item ? 'primary' : tagged.has(item) ? 'warning' : 'default'}
              onClick={() => onPageChange(item)}
            />
          ))}
        </Box>
      )}
      {meta.available && (
        <Box sx={{ overflow: 'auto', maxHeight: popout ? 'calc(100vh - 140px)' : 640, border: 1, borderColor: 'divider', bgcolor: '#f7f7f4' }}>
          <Box
            component="img"
            src={src}
            alt={`Schematic page ${current}`}
            sx={{
              display: 'block',
              transform: `scale(${zoom})`,
              transformOrigin: 'top left',
              maxWidth: zoom === 1 ? '100%' : 'none',
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default SchematicViewer;
